import { IntroCard } from '@/components/shared/IntroCard';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Input } from '@/components/ui/input';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Textarea } from '@/components/ui/textarea';
import { useToast } from '@/components/ui/use-toast';
import { useLicense } from '@/hooks/useLicense';
import { useProjectSettings } from '@/hooks/useProjectSettings';
import { useCreateTemplateMutation, useDeleteTemplateMutation, useTemplatesQuery } from '@/hooks/useTemplateQueries';
import { format } from 'date-fns';
import { AnimatePresence, motion } from 'framer-motion';
import {
  Copy,
  FileText,
  LayoutTemplate, Lightbulb,
  Loader2,
  MoreHorizontal,
  Pencil,
  Plus,
  Search,
  Sparkles,
  Trash2
} from 'lucide-react';
import { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

// --- Types ---
interface TemplateItem {
  id: number;
  name: string;
  description: string | null;
  created_at: string;
  updated_at: string | null;
  is_public: boolean;
}

// --- Component ---
// Template List Page - Table View
const TemplateListPage = () => {
  const navigate = useNavigate();
  const { projectId } = useParams<{ projectId: string }>();
  const { toast } = useToast();
  const { hasAIFeatures } = useLicense();

  // State
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [isAddingTemplate, setIsAddingTemplate] = useState<boolean>(false);
  const [newTemplateName, setNewTemplateName] = useState<string>('');
  const [newTemplateDescription, setNewTemplateDescription] = useState<string>('');
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [isAITemplateDialogOpen, setIsAITemplateDialogOpen] = useState<boolean>(false);

  // Delete dialog state
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState<boolean>(false);
  const [templateToDelete, setTemplateToDelete] = useState<{ id: number; name: string } | null>(null);

  // Queries
  const projectIdNum = projectId ? parseInt(projectId) : undefined;
  const { data: templatesData, isLoading: isTemplatesLoading } = useTemplatesQuery(projectIdNum);

  // Settings
  const { useIntroDismissal } = useProjectSettings(projectId || '');
  const { isDismissed: isIntroDismissed, dismiss: dismissIntro } = useIntroDismissal('templates_intr_v2');

  // Mutations
  const createTemplateMutation = useCreateTemplateMutation();
  const deleteTemplateMutation = useDeleteTemplateMutation();

  // Filter templates
  const filteredTemplates = (templatesData || []).filter(template => {
    const term = searchQuery.toLowerCase();
    return (
      template.name.toLowerCase().includes(term) ||
      (template.description && template.description.toLowerCase().includes(term))
    );
  });

  // Actions
  const addTemplate = async () => {
    if (!newTemplateName.trim() || !projectId) return;

    try {
      setIsSubmitting(true);
      const createdTemplate = await createTemplateMutation.mutateAsync({
        name: newTemplateName.trim(),
        description: newTemplateDescription || undefined,
        is_public: true,
        project_id: parseInt(projectId)
      });

      setNewTemplateName('');
      setNewTemplateDescription('');
      setIsAddingTemplate(false);

      toast({
        title: "Template created",
        description: `Template "${createdTemplate.name}" created successfully.`,
      });

      navigate(`/projects/${projectId}/form-builder/editor?template=${createdTemplate.id}`);
    } catch (error) {
      console.error('Error creating template:', error);
      toast({
        title: "Error",
        description: "Failed to create template.",
        variant: "destructive"
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const deleteTemplate = async () => {
    if (!projectId || !templateToDelete) return;
    try {
      await deleteTemplateMutation.mutateAsync({
        templateId: templateToDelete.id,
        projectId: parseInt(projectId)
      });
      toast({ title: "Template deleted", description: "The template has been permanently deleted." });
      setIsDeleteDialogOpen(false);
      setTemplateToDelete(null);
    } catch (error) {
      toast({ title: "Error", description: "Failed to delete template.", variant: "destructive" });
    }
  };

  const duplicateTemplate = async (template: TemplateItem) => {
    if (!projectId) return;
    try {
      await createTemplateMutation.mutateAsync({
        name: `${template.name} (Copy)`,
        description: template.description || undefined,
        is_public: template.is_public,
        project_id: parseInt(projectId)
      });
      toast({ title: "Template duplicated", description: `Copy of "${template.name}" created.` });
    } catch (error) {
      toast({ title: "Error", description: "Failed to duplicate template.", variant: "destructive" });
    }
  };

  // --- Render ---
  return (
    <div className="min-h-screen bg-background text-foreground py-8 px-4 sm:px-8">

      {/* Header */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-8">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">Templates</h1>
          <p className="text-muted-foreground mt-1 text-sm">
            Manage your content structures and forms.
          </p>
        </div>

        <div className="flex items-center gap-3 w-full sm:w-auto">
          <div className="relative flex-1 sm:w-64">
            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search templates..."
              className="pl-9 h-9 bg-muted/50 border-border focus:border-primary/20"
            />
          </div>

          <div className="flex items-center gap-2">
            {hasAIFeatures && (
              <Button
                variant="outline"
                size="sm"
                onClick={() => setIsAITemplateDialogOpen(true)}
                className="h-9 hidden sm:flex gap-2"
              >
                <Sparkles className="h-4 w-4 text-indigo-400" />
                <span>AI Generate</span>
              </Button>
            )}
            <Button
              size="sm"
              onClick={() => setIsAddingTemplate(true)}
              className="h-9 bg-primary text-primary-foreground hover:bg-primary/90"
            >
              <Plus className="mr-2 h-4 w-4" />
              New Template
            </Button>
          </div>
        </div>
      </div>

      {/* Intro Card */}
      {!isIntroDismissed && !isTemplatesLoading && filteredTemplates.length === 0 && (
        <IntroCard
          title="Start with a Structure"
          description="Templates help you organize your data with consistent fields and layouts. Create reusable character sheets, location profiles, or plot outlines to maintain continuity across your story."
          onClose={dismissIntro}
          actions={
            <div className="space-y-4">
              <div className="space-y-2">
                <h4 className="text-[10px] font-bold uppercase tracking-[0.2em] text-muted-foreground/80">Pro Tip</h4>
                <p className="text-xs text-muted-foreground leading-relaxed flex gap-2">
                  <Lightbulb className="h-3 w-3 text-amber-500 mt-0.5 shrink-0" />
                  Use AI to generate starter templates for your genre.
                </p>
              </div>
              <Button
                variant="outline"
                className="w-full justify-start text-xs h-9"
                onClick={() => setIsAddingTemplate(true)}
              >
                <Plus className="h-3 w-3 mr-2" />
                Create Custom Template
              </Button>
            </div>
          }
        >
          <div className="flex gap-4 mt-4">
            <div className="flex items-center gap-2 text-xs text-muted-foreground bg-muted/30 px-3 py-1.5 rounded-full border border-border/50">
              <LayoutTemplate className="h-3 w-3" />
              <span>Consistent Data</span>
            </div>
            <div className="flex items-center gap-2 text-xs text-muted-foreground bg-muted/30 px-3 py-1.5 rounded-full border border-border/50">
              <Sparkles className="h-3 w-3" />
              <span>AI Integrated</span>
            </div>
          </div>
        </IntroCard>
      )}

      {/* Content */}
      <div className="space-y-4">
        {isTemplatesLoading ? (
          <div className="flex flex-col items-center justify-center py-24 text-muted-foreground">
            <Loader2 className="h-8 w-8 animate-spin mb-4" />
            <p>Loading templates...</p>
          </div>
        ) : filteredTemplates.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-24 text-center border rounded-lg border-dashed border-border bg-muted/5">
            <div className="h-12 w-12 rounded-full bg-muted/10 flex items-center justify-center mb-4">
              <FileText className="h-6 w-6 text-muted-foreground" />
            </div>
            <h3 className="text-lg font-medium mb-1">No templates found</h3>
            <p className="text-sm text-muted-foreground mb-6 max-w-sm mx-auto">
              {searchQuery ? `No matches for "${searchQuery}"` : "Create your first template to get started."}
            </p>
            <Button onClick={() => setIsAddingTemplate(true)} variant="outline">
              Create Manually
            </Button>
          </div>
        ) : (
          <div className="rounded-md border border-border bg-card overflow-hidden">
            <Table>
              <TableHeader>
                <TableRow className="hover:bg-transparent border-border">
                  <TableHead className="w-[40%]">Name</TableHead>
                  <TableHead className="w-[40%]">Description</TableHead>
                  <TableHead className="w-[15%]">Last Modified</TableHead>
                  <TableHead className="w-[5%]"></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                <AnimatePresence initial={false}>
                  {filteredTemplates.map((template) => (
                    <motion.tr
                      key={template.id}
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      exit={{ opacity: 0 }}
                      layout
                      className="group border-b border-border hover:bg-muted/50 transition-colors cursor-pointer"
                      onClick={() => navigate(`/projects/${projectId}/form-builder/editor?template=${template.id}`)}
                    >
                      <TableCell className="font-medium text-foreground py-4">
                        <div className="flex items-center gap-3">
                          <FileText className="h-4 w-4 text-muted-foreground/50" />
                          {template.name}
                        </div>
                      </TableCell>
                      <TableCell className="text-muted-foreground">
                        {template.description ? (
                          <span className="line-clamp-1">{template.description}</span>
                        ) : (
                          <span className="opacity-30 italic">No description</span>
                        )}
                      </TableCell>
                      <TableCell className="text-muted-foreground text-sm font-mono">
                        {template.updated_at
                          ? format(new Date(template.updated_at), 'MMM d, yyyy')
                          : format(new Date(template.created_at), 'MMM d, yyyy')
                        }
                      </TableCell>
                      <TableCell>
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-8 w-8 opacity-0 group-hover:opacity-100 transition-opacity"
                              onClick={(e) => e.stopPropagation()}
                            >
                              <MoreHorizontal className="h-4 w-4 text-muted-foreground" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end" className="w-48 bg-popover border-border text-popover-foreground">
                            <DropdownMenuItem onClick={(e) => { e.stopPropagation(); navigate(`/projects/${projectId}/form-builder/editor?template=${template.id}`); }}>
                              <Pencil className="mr-2 h-4 w-4" /> Edit
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={(e) => { e.stopPropagation(); duplicateTemplate(template); }}>
                              <Copy className="mr-2 h-4 w-4" /> Duplicate
                            </DropdownMenuItem>
                            <DropdownMenuSeparator className="bg-border" />
                            <DropdownMenuItem
                              className="text-red-400 focus:text-red-400 focus:bg-red-400/10"
                              onClick={(e) => { e.stopPropagation(); setTemplateToDelete(template); setIsDeleteDialogOpen(true); }}
                            >
                              <Trash2 className="mr-2 h-4 w-4" /> Delete
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </TableCell>
                    </motion.tr>
                  ))}
                </AnimatePresence>
              </TableBody>
            </Table>
          </div>
        )}
      </div>

      {/* Dialogs */}
      <Dialog open={isAddingTemplate} onOpenChange={setIsAddingTemplate}>
        <DialogContent className="sm:max-w-[600px] sm:rounded-2xl border-border/60 shadow-2xl p-0 overflow-hidden gap-0">
          <DialogHeader className="p-8 pb-6 border-b border-border/40 bg-muted/5 space-y-1">
            <DialogTitle className="text-2xl font-semibold text-foreground tracking-tight">New Template</DialogTitle>
            <DialogDescription className="text-base text-muted-foreground leading-relaxed">
              Create a blank template to start building.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-6 px-8 py-8">
            <div className="space-y-2.5">
              <label className="text-sm font-medium text-foreground/80">Name</label>
              <Input
                value={newTemplateName}
                onChange={(e) => setNewTemplateName(e.target.value)}
                placeholder="e.g. Character Sheet"
                className="h-11 rounded-xl bg-muted/30 border-muted-foreground/10 focus:border-primary/20 focus:ring-primary/20 transition-all font-medium text-foreground placeholder:text-muted-foreground/50 shadow-sm"
                autoFocus
              />
            </div>
            <div className="space-y-2.5">
              <label className="text-sm font-medium text-foreground/80">Description</label>
              <Textarea
                value={newTemplateDescription}
                onChange={(e) => setNewTemplateDescription(e.target.value)}
                placeholder="Optional description"
                className="min-h-[100px] resize-none rounded-xl bg-muted/30 border-muted-foreground/10 focus:border-primary/20 focus:ring-primary/20 transition-all font-medium text-foreground placeholder:text-muted-foreground/50 shadow-sm"
              />
            </div>
          </div>
          <DialogFooter className="p-6 bg-muted/5 border-t border-border/40">
            <Button
              variant="ghost"
              onClick={() => setIsAddingTemplate(false)}
              className="min-w-[100px] rounded-xl hover:bg-muted/50"
            >
              Cancel
            </Button>
            <Button
              onClick={addTemplate}
              disabled={!newTemplateName.trim() || isSubmitting}
              className="min-w-[120px] rounded-xl font-medium shadow-sm active:scale-95 transition-all text-base"
            >
              {isSubmitting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Create Template
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={isDeleteDialogOpen} onOpenChange={setIsDeleteDialogOpen}>
        <DialogContent className="sm:max-w-[400px] bg-popover border-border text-popover-foreground">
          <DialogHeader>
            <DialogTitle className="text-red-400 flex items-center gap-2">
              <Trash2 className="h-5 w-5" />
              Delete Template?
            </DialogTitle>
            <DialogDescription className="text-muted-foreground pt-2">
              This will permanently delete <strong>{templateToDelete?.name}</strong>.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="mt-4 gap-2 sm:gap-0">
            <Button variant="ghost" onClick={() => setIsDeleteDialogOpen(false)} className="hover:bg-white/5">Cancel</Button>
            <Button variant="destructive" onClick={deleteTemplate}>Delete Template</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default TemplateListPage;